import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, ShieldX, RefreshCw, Download, Plus, CheckCircle2, AlertTriangle, XCircle,
  MinusCircle, Clock, ChevronRight, Lock, Users, Building2, Database, ScrollText, Server, ClipboardList,
  Activity, Zap, ListChecks, Search, KeyRound, X, CalendarClock, Wifi, Settings2,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import api from "@/lib/api";
import { Spinner } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Breadcrumb } from "@/components/Breadcrumb";
import { STATUS_META } from "@/lib/securityStatus";
import SecurityConfigModal from "@/components/SecurityConfigModal";
import BackupCoverageCard from "./qa/BackupCoverageCard";

const C = { safe: "#A3E635", warning: "#fbbf24", critical: "#f87171", open: "#6b7280", neutral: "#A3E635" };
const DOMAIN_ICON = { login: Lock, roles: Users, tenants: Building2, backup: Database, audit: ScrollText, system: Server, special: ShieldCheck };
const FOCUS_LABEL = { login: "Login & Zugriff", roles: "Rollen & Rechte", tenants: "Kundenkonten & Mandanten", backup: "Backup & Wiederherstellung", audit: "Audit & Nachvollziehbarkeit" };

const nf = (n) => (typeof n === "number" ? n.toLocaleString("de-CH") : n);
const fmtTs = (iso) => {
  if (!iso) return "—";
  try { const d = new Date(iso); return `${d.toLocaleDateString("de-CH")}, ${d.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`; }
  catch { return iso.slice(0, 16); }
};

export default function QA() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canConfig = user?.role === "super_admin";
  const nav = useNavigate();
  const location = useLocation();
  const focus = new URLSearchParams(location.search).get("focus");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [plan, setPlan] = useState(false);
  const [highlightKey, setHighlightKey] = useState(null);
  const [config, setConfig] = useState(null);
  const domainsRef = useRef(null);

  const load = (silent) => {
    setBusy(true);
    return api.get("/admin/qa").then((r) => { setData(r.data); if (!silent) toast.success(t("Sicherheitscheck aktualisiert")); })
      .catch(() => toast.error(t("Konnte nicht geladen werden"))).finally(() => setBusy(false));
  };
  useEffect(() => { load(true); }, []);

  useEffect(() => {
    if (!focus || !data) return;
    const el = document.getElementById(`qa-domain-anchor-${focus}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightKey(focus);
    const tmo = setTimeout(() => setHighlightKey(null), 2600);
    return () => clearTimeout(tmo);
  }, [data, focus]);

  const runScan = async () => {
    setRunning(true);
    try { const r = await api.post("/admin/qa/run", { type: "manual" }); setData(r.data); toast.success(t("Neue Prüfung abgeschlossen")); }
    catch { toast.error(t("Prüfung fehlgeschlagen")); } finally { setRunning(false); }
  };
  const exportReport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/qa/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `sicherheit-qa-bericht-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Bericht exportiert & protokolliert"));
    } catch { toast.error(t("Export fehlgeschlagen")); } finally { setExporting(false); }
  };
  const scrollDomains = () => domainsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const goAction = (action) => {
    if (!action) return;
    if (action === "scan" || action === "updates") return runScan();
    if (action === "fix_critical") return scrollDomains();
    nav(action);
  };

  if (!data) return <Spinner />;
  const sb = data.status_bar; const k = data.kpis;
  const ampel = sb.status === "critical" ? { Icon: ShieldX, ring: "border-red-500/40 bg-gradient-to-r from-red-500/[0.12] to-transparent", text: "text-red-300" }
    : sb.status === "warning" ? { Icon: ShieldAlert, ring: "border-amber-500/40 bg-gradient-to-r from-amber-500/[0.10] to-transparent", text: "text-amber-300" }
    : sb.status === "open" ? { Icon: ShieldAlert, ring: "border-gray-500/40 bg-white/[0.03]", text: "text-gray-400" }
    : { Icon: ShieldCheck, ring: "border-[#A3E635]/40 bg-gradient-to-r from-[#A3E635]/[0.10] to-transparent", text: "text-[#A3E635]" };

  return (
    <div className="fade-in w-full" data-testid="qa-page">
      <Breadcrumb testid="qa-breadcrumb" items={[
        { label: t("System") },
        focus ? { label: t("Sicherheit & QA"), to: "/admin/qa" } : { label: t("Sicherheit & QA") },
        ...(focus && FOCUS_LABEL[focus] ? [{ label: t(FOCUS_LABEL[focus]) }] : []),
      ]} />
      {/* HEADER */}
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-white">{t("Sicherheit & QA")}</h1>
          <p className="text-sm text-gray-400 mt-1">{t("Security & Quality Center für Super Admins – überwachen, prüfen, absichern.")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-gray-400 mr-1" data-testid="qa-live">
            <span className="flex items-center gap-1.5 text-[#A3E635] font-bold"><span className="w-1.5 h-1.5 rounded-full bg-[#A3E635]" /> {t("Datenabfrage")}</span>
            <span>{t("Letzte Aktualisierung:")} {fmtTs(data.checked_at)}</span>
            <button onClick={() => load()} disabled={busy} data-testid="qa-refresh" className="p-1 hover:text-white"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /></button>
          </div>
          <button onClick={() => load()} disabled={busy} data-testid="qa-check" className="bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold transition-colors">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {t("Check prüfen")}
          </button>
          <button onClick={exportReport} disabled={exporting} data-testid="qa-export" className="bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold transition-colors">
            <Download className={`w-4 h-4 ${exporting ? "animate-pulse" : ""}`} /> {t("Bericht exportieren")}
          </button>
          <button onClick={runScan} disabled={running} data-testid="qa-run" className="bg-[#A3E635] hover:bg-[#BEF264] text-black py-2.5 px-5 rounded-xl flex items-center gap-2 text-sm font-bold transition-colors disabled:opacity-60">
            {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t("Neue Prüfung starten")}
          </button>
        </div>
      </div>

      {/* SECURITY STATUS BAR */}
      <div className={`rounded-2xl border ${ampel.ring} px-6 py-5 flex flex-col lg:flex-row lg:items-center gap-6 mb-6`} data-testid="qa-statusbar">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className={`w-14 h-14 rounded-2xl border flex items-center justify-center shrink-0 ${ampel.ring} ${ampel.text}`}><ampel.Icon className="w-7 h-7" /></div>
          <div className="min-w-0">
            <div className="text-xl font-black tracking-tight text-white">{t("Sicherheitsstatus:")} <span className={ampel.text}>{t(sb.status_label)}</span></div>
            <div className="text-sm text-gray-400 mt-0.5">
              {sb.critical} {t("kritische Punkte")} · {sb.warnings} {t("Warnungen")} · {sb.open} {t("offen")} · {sb.critical > 0 || sb.warnings > 0 ? t("Handlungsbedarf erforderlich") : sb.open > 0 ? t("Offen") : t("kein akuter Handlungsbedarf")}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 lg:gap-6">
          <StatNum value={sb.critical} label={t("Kritisch")} color={C.critical} testid="qa-sb-critical" />
          <StatNum value={sb.warnings} label={t("Warnungen")} color={C.warning} testid="qa-sb-warnings" />
          <StatNum value={sb.safe} label={t("Sicher")} color={C.safe} testid="qa-sb-safe" />
          <StatNum value={`${sb.coverage}%`} label={t("Abdeckung")} color="#38bdf8" testid="qa-sb-coverage" />
          <button onClick={scrollDomains} data-testid="qa-sb-details" className="bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold transition-colors whitespace-nowrap">
            <ListChecks className="w-4 h-4" /> {t("Details anzeigen")}
          </button>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-4 mb-6" data-testid="qa-kpis">
        <Kpi tid="kritische_punkte" label={t("Kritische Punkte")} kpi={k.kritische_punkte} color={C.critical} />
        <Kpi tid="warnungen_offen" label={t("Warnungen offen")} kpi={k.warnungen_offen} color={C.warning} />
        <Kpi tid="erfolgreiche_checks" label={t("Erfolgreiche Checks")} kpi={k.erfolgreiche_checks} color={C.safe} />
        <Kpi tid="abdeckung_systeme" label={t("Aktuelle Websitemessungen")} kpi={k.abdeckung_systeme} color="#38bdf8" />
        <Kpi tid="offene_aufgaben" label={t("Offene Aufgaben")} kpi={k.offene_aufgaben} color="#c084fc" />
        <KpiTime tid="letzte_pruefung" label={t("Letzte Prüfung")} kpi={k.letzte_pruefung} />
      </div>

      {/* 3 WORK PANELS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
        {/* Wichtig heute */}
        <Panel className="lg:col-span-4" icon={ClipboardList} title={t("Wichtig heute")}
          right={<span className="text-[11px] font-bold text-red-300 bg-red-500/10 border border-red-500/30 px-2 py-0.5 rounded-full">{sb.critical} {t("kritische Aktionen")}</span>} testid="qa-wichtig">
          <div className="space-y-2">
            {data.wichtig_heute.length === 0 && <Empty label={t("Keine dringenden Punkte – alles im grünen Bereich.")} />}
            {data.wichtig_heute.map((w) => {
              const m = STATUS_META[w.severity] || STATUS_META.warning;
              return (
                <button key={w.id} onClick={() => goAction(w.action)} data-testid={`qa-wichtig-${w.id}`}
                  className="w-full text-left flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-all group">
                  <m.Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: m.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white truncate">{w.title}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide shrink-0" style={{ color: m.color, background: `${m.color}1a` }}>{m.label}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">{w.detail}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#A3E635] shrink-0 mt-0.5" />
                </button>
              );
            })}
          </div>
          <button onClick={() => nav("/admin/security?tab=audit")} data-testid="qa-wichtig-all" className="mt-3 w-full text-center text-xs font-semibold text-[#A3E635] hover:underline py-1">{t("Alle Aufgaben anzeigen")} →</button>
        </Panel>

        {/* Login-Schutz 24h */}
        <Panel className="lg:col-span-5" icon={Activity} title={t("Login-Schutz 24h")} testid="qa-login">
          <div className="grid grid-cols-4 gap-3 mb-4">
            <MiniStat value={nf(data.login_24h.attempts)} label={t("Login-Versuche")} delta={data.login_24h.delta_attempts} color="#38bdf8" testid="qa-login-attempts" />
            <MiniStat value={nf(data.login_24h.failed)} label={t("Fehlgeschlagen")} delta={data.login_24h.delta_failed} color={C.critical} invert testid="qa-login-failed" />
            <MiniStat value={nf(data.login_24h.blocked_ips)} label={t("Blockierte IPs")} color={C.warning} testid="qa-login-blocked" />
            <MiniStat value={data.login_24h.success_rate == null ? "—" : `${data.login_24h.success_rate}%`} label={t("Erfolgsquote")} color={C.safe} testid="qa-login-rate" />
          </div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{t("Login-Versuche (letzte 24h)")}</div>
          <div className="h-52" data-testid="qa-login-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.login_24h.hourly} margin={{ top: 6, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#6b7280" }} interval={3} tickLine={false} axisLine={{ stroke: "#ffffff15" }} />
                <YAxis tick={{ fontSize: 9, fill: "#6b7280" }} tickLine={false} axisLine={false} width={28} />
                <Tooltip contentStyle={{ background: "#15151b", border: "1px solid #ffffff20", borderRadius: 10, fontSize: 12 }} labelStyle={{ color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                <Bar dataKey="success" name={t("Erfolgreich")} stackId="a" fill={C.safe} radius={[0, 0, 0, 0]} />
                <Bar dataKey="failed" name={t("Fehlgeschlagen")} stackId="a" fill={C.critical} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <button onClick={() => nav("/admin/security?tab=audit")} data-testid="qa-login-open" className="mt-2 w-full text-center text-xs font-semibold text-[#A3E635] hover:underline py-1">{t("Login-Analyse öffnen")} →</button>
        </Panel>

        {/* Sofort handeln */}
        <Panel className="lg:col-span-3" icon={Zap} title={t("Sofort handeln")} testid="qa-sofort">
          <div className="space-y-2">
            {data.sofort_handeln.map((s) => {
              const isCrit = s.severity === "critical" && sb.critical > 0;
              const Ico = { "fix-critical": ShieldAlert, scan: Search, updates: RefreshCw, backups: Database, audit: ScrollText, access: Users }[s.id] || ChevronRight;
              return (
                <button key={s.id} onClick={() => goAction(s.action)} data-testid={`qa-sofort-${s.id}`}
                  className={`w-full text-left text-sm border rounded-xl px-3 py-3 flex items-center gap-3 group transition-all ${isCrit ? "border-red-500/30 bg-red-500/[0.06] hover:bg-red-500/[0.12]" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/20"}`}>
                  <Ico className="w-4 h-4 shrink-0" style={{ color: isCrit ? C.critical : C.safe }} />
                  <span className="flex-1 font-medium text-gray-200">{s.label}</span>
                  <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#A3E635]" />
                </button>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* DOMAIN BOARD */}
      <div ref={domainsRef} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-7 gap-4 mb-6" data-testid="qa-domains">
        {data.domains.map((d) => <DomainCard key={d.key} d={d} t={t} nav={nav} onRecheck={() => load()} onConfigure={canConfig ? (code, title) => setConfig({ code, title }) : undefined} highlight={highlightKey === d.key} />)}
      </div>

      {canConfig && <BackupCoverageCard t={t} />}

      {/* FOOTER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-4 border-t border-white/10" data-testid="qa-footer">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-xs text-gray-500">{t("Status:")}</span>
          {[["safe", t("Sicher")], ["warning", t("Warnung")], ["critical", t("Kritisch")], ["open", t("Offen")]].map(([s, l]) => (
            <span key={s} className="flex items-center gap-1.5 text-xs text-gray-400"><span className="w-2 h-2 rounded-full" style={{ background: C[s] }} /> {l}</span>
          ))}
          <span className="flex items-center gap-1.5 text-xs text-gray-400 ml-2" data-testid="qa-systemstatus">
            <Wifi className="w-3.5 h-3.5 text-gray-400" /> {t(data.system_status.label)} · {t("Gemessen")} {data.system_status.instances_observed ?? "—"}/{data.system_status.instances_total} · {t("Erreichbar")} {data.system_status.instances_available}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 flex items-center gap-1.5">{t("Automatische Prüfungen:")} <span className="text-gray-400 font-bold flex items-center gap-1">{data.tools.auto_checks_active ? t("Aktiv") : t("Nicht nachgewiesen")}</span></span>
          <button onClick={() => setPlan(true)} data-testid="qa-plan" className="bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold transition-colors">
            <CalendarClock className="w-4 h-4 text-[#A3E635]" /> {t("Prüfplan verwalten")}
          </button>
        </div>
      </div>

      {plan && <PruefplanModal data={data} t={t} onClose={() => setPlan(false)} onRun={() => { setPlan(false); runScan(); }} />}
      {config && <SecurityConfigModal code={config.code} title={config.title} onClose={() => setConfig(null)} onSaved={() => load(true)} />}
    </div>
  );
}

const StatNum = ({ value, label, color, testid }) => (
  <div className="text-center" data-testid={testid}>
    <div className="text-2xl font-black leading-none" style={{ color }}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
  </div>
);

function Sparkline({ data, color }) {
  if (!data || data.length < 2) return null;
  const w = 84, h = 26, min = Math.min(...data), max = Math.max(...data), span = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / span) * (h - 4) - 2}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
    </svg>
  );
}

function Kpi({ label, kpi, color, tid }) {
  const up = (kpi.delta ?? 0) >= 0;
  return (
    <div className="bg-[#15151b] border border-white/[0.07] rounded-2xl p-4 hover:border-white/15 transition-colors" data-testid={`qa-kpi-${tid}`}>
      <div className="text-[11px] font-semibold text-gray-400 mb-1.5">{label}</div>
      <div className="flex items-end justify-between gap-2 flex-wrap">
        <div className="text-3xl font-black leading-none" style={{ color }}>{nf(kpi.value)}{kpi.unit || ""}</div>
        <Sparkline data={kpi.spark} color={color} />
      </div>
      {kpi.delta_label
        ? <div className="text-[10px] mt-2" style={{ color: up ? C.safe : C.critical }}>{kpi.delta_label}</div>
        : <div className="text-[10px] text-gray-600 mt-2">{kpi.delta === 0 ? "stabil" : "—"}</div>}
      <div className="text-[10px] text-gray-500 mt-0.5">{kpi.sub}</div>
    </div>
  );
}

function KpiTime({ label, kpi, tid }) {
  return (
    <div className="bg-[#15151b] border border-white/[0.07] rounded-2xl p-4 hover:border-white/15 transition-colors" data-testid={`qa-kpi-${tid}`}>
      <div className="text-[11px] font-semibold text-gray-400 mb-1.5">{label}</div>
      <div className="flex items-end justify-between">
        <div className="text-3xl font-black leading-none text-white">{kpi.value}</div>
        <Clock className="w-6 h-6 text-gray-600" />
      </div>
      <div className="text-[10px] text-gray-500 mt-2">{kpi.sub}</div>
      <div className="text-[10px] text-gray-600 mt-0.5">{kpi.duration && kpi.duration !== "—" ? `Dauer: ${kpi.duration}` : "—"}</div>
    </div>
  );
}

function MiniStat({ value, label, delta, color, invert, testid }) {
  const has = delta !== undefined && delta !== null;
  const good = invert ? delta <= 0 : delta >= 0;
  return (
    <div data-testid={testid}>
      <div className="text-2xl font-black leading-none" style={{ color }}>{value}</div>
      <div className="text-[10px] text-gray-500 mt-1">{label}</div>
      {has && <div className="text-[10px] mt-0.5" style={{ color: good ? C.safe : C.critical }}>{delta >= 0 ? "+" : ""}{delta}%</div>}
    </div>
  );
}

function Panel({ className = "", icon: Icon, title, right, children, testid }) {
  return (
    <div className={`bg-[#121217] border border-white/[0.07] rounded-2xl p-5 flex flex-col ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold tracking-tight text-white flex items-center gap-2 text-sm"><Icon className="w-4 h-4 text-[#A3E635]" /> {title}</h3>
        {right}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

const Empty = ({ label }) => <div className="text-xs text-gray-600 bg-white/[0.02] border border-white/10 rounded-xl px-3 py-4 text-center">{label}</div>;

function DomainCard({ d, t, nav, onRecheck, onConfigure, highlight }) {
  const Icon = DOMAIN_ICON[d.key] || ShieldCheck;
  const issues = d.critical + d.warning;
  const badge = d.critical > 0 ? { c: C.critical, txt: `${d.critical}/${d.total} kritisch` }
    : d.warning > 0 ? { c: C.warning, txt: `${d.warning}/${d.total} Warnung` }
    : d.open > 0 ? { c: C.open, txt: `${d.open}/${d.total} Hinweise` }
    : { c: C.safe, txt: `${d.safe}/${d.total} sicher` };
  return (
    <div id={`qa-domain-anchor-${d.key}`} className={`bg-[#121217] border rounded-2xl p-4 flex flex-col scroll-mt-24 transition-all duration-500 ${highlight ? "ring-2 ring-[#A3E635] border-[#A3E635]/50 shadow-[0_0_24px_rgba(163,230,53,0.18)]" : d.critical > 0 ? "border-red-500/25" : "border-white/[0.07]"}`} data-testid={`qa-domain-${d.key}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-sm text-white flex items-center gap-2"><Icon className="w-4 h-4 text-[#A3E635]" /> {d.title}</h4>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ color: badge.c, background: `${badge.c}1a` }}>{badge.txt}</span>
      </div>
      <div className="space-y-2 flex-1">
        {d.checks.map((c, i) => {
          const m = STATUS_META[c.status] || STATUS_META.open;
          return (
            <div key={i} className="flex items-start gap-2" data-testid={`qa-check-${d.key}-${i}`} title={c.description}>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold text-gray-200 leading-tight truncate">{c.title}</div>
                <div className="text-[10px] text-gray-500 truncate">{c.description}</div>
              </div>
              <span className="flex items-center gap-1 text-[10px] font-semibold shrink-0" style={{ color: m.color }}>
                <m.Icon className="w-3.5 h-3.5" /> {m.label}
              </span>
              {c.action_code && onConfigure && (
                <button onClick={() => onConfigure(c.action_code, c.title)} data-testid={`qa-config-${c.action_code}`}
                  title={c.action_label || t("Konfigurieren")}
                  className="shrink-0 p-1 rounded-md text-gray-500 hover:text-[#A3E635] hover:bg-white/10 transition-colors"><Settings2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          );
        })}
      </div>
      <button onClick={() => nav(d.action)} data-testid={`qa-domain-all-${d.key}`}
        className="mt-3 w-full text-center text-[11px] font-semibold text-[#A3E635] hover:underline py-1 border-t border-white/5 pt-2.5">{t(d.action_label)} →</button>
    </div>
  );
}

function PruefplanModal({ data, t, onClose, onRun }) {
  const items = [
    { label: t("Automatische Tages-Backups"), cadence: t("Täglich · 06:00"), icon: Database },
    { label: t("Login- & Sicherheitsüberwachung"), cadence: t("Laufend (Echtzeit)"), icon: Activity },
    { label: t("Audit-Log-Integritätsprüfung"), cadence: t("Bei jeder Prüfung"), icon: ScrollText },
    { label: t("Mandanten- & Rollen-Checks"), cadence: t("Bei jeder Prüfung"), icon: Users },
    { label: t("System- & Backup-Checks"), cadence: t("Bei jeder Prüfung"), icon: Server },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="qa-plan-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#121217] border border-white/10 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-bold flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#A3E635]" /> {t("Prüfplan verwalten")}</h3>
          <button onClick={onClose} data-testid="qa-plan-close" className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 text-sm text-[#A3E635] bg-[#A3E635]/[0.07] border border-[#A3E635]/25 rounded-xl px-4 py-2.5 mb-4">
            <ShieldCheck className="w-4 h-4" /> {t("Automatische Prüfungen sind aktiv.")}
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/[0.02] border border-white/10 rounded-xl px-4 py-3" data-testid={`qa-plan-item-${i}`}>
                <it.icon className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-200 flex-1">{it.label}</span>
                <span className="text-[11px] text-gray-500">{it.cadence}</span>
              </div>
            ))}
          </div>
          <button onClick={onRun} data-testid="qa-plan-run" className="mt-5 w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> {t("Jetzt vollständige Prüfung ausführen")}
          </button>
        </div>
      </div>
    </div>
  );
}

